import { useEffect, useId, useMemo, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { BrandAssembly } from '../brand/BrandAssembly';
import { ArrowGlyph, SectionMark } from '../components/Cta';
import { SiteFooter } from '../components/SiteFooter';
import { SiteHeader } from '../components/SiteHeader';
import { CONTACT_EMAIL, SITE } from '../content/site';
import { START, START_IDS, isStartId, takePrefill } from '../content/start';
import { LocaleProvider, type Locale } from '../i18n/locale';
import { CATEGORY_IDS, EMPTY, categories, scopeLabel, scopes, steps, categoryLabel, summaryText, validate, type Draft, type Errors } from './model';
import { INTAKE } from './content';

/**
 * Public project intake — front-end structure only.
 * Backend public submission is disabled by policy (docs/technical/turnstile-intake-contract.md);
 * this page never calls the API and never enables it. Hand-off is a controlled channel:
 * a configured commercial mailbox (VITE_PUBLIC_CONTACT_EMAIL) or a copy/download of the
 * structured summary. No price, timeline or importability is promised. Localized (es-CO, en).
 */
const DRAFT_KEY = 'cx-intake-draft';

export function IntakePage({ locale = 'es-CO' }: { locale?: Locale }) {
  const copy = INTAKE[locale]; const STEPS = steps(locale); const SCOPES = scopes(locale);
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [step, setStep] = useState(0);
  const [errors, setErrors] = useState<Errors>({});
  const [copied, setCopied] = useState(false);
  const heading = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    // Restore only non-personal fields; contact data is never persisted in the browser.
    try {
      const saved = JSON.parse(localStorage.getItem(DRAFT_KEY) ?? 'null') as Partial<Draft> | null;
      const prefill = takePrefill();
      const restored = { ...(saved ?? {}) }; if (restored.category && !(CATEGORY_IDS as readonly string[]).includes(restored.category)) restored.category = '';
      if (restored.start && !isStartId(restored.start)) restored.start = '';
      setDraft(current => ({ ...current, ...restored, ...(prefill?.need ? { need: prefill.need } : {}), ...(prefill?.start ? { start: prefill.start } : {}) }));
    } catch {}
  }, []);
  useEffect(() => {
    const { start, need, category, scope, quantity, reference, city, date } = draft;
    try { localStorage.setItem(DRAFT_KEY, JSON.stringify({ start, need, category, scope, quantity, reference, city, date })); } catch {}
  }, [draft]);

  const set = <K extends keyof Draft>(key: K, value: Draft[K]) => { setDraft(current => ({ ...current, [key]: value })); setErrors(current => ({ ...current, [key]: undefined })); };
  const go = (next: number) => { setStep(next); setCopied(false); requestAnimationFrame(() => heading.current?.focus()); };
  const advance = (event: FormEvent) => {
    event.preventDefault();
    const found = validate(step, draft, locale); setErrors(found);
    const first = Object.keys(found)[0];
    if (first) { document.getElementById(`intake-${first}`)?.focus(); return; }
    go(Math.min(STEPS.length - 1, step + 1));
  };
  const summary = useMemo(() => summaryText(draft, locale), [draft, locale]);
  const copySummary = async () => { try { await navigator.clipboard.writeText(summary); setCopied(true); } catch { setCopied(false); } };
  const download = () => {
    const url = URL.createObjectURL(new Blob([summary], { type: 'text/plain;charset=utf-8' }));
    const link = document.createElement('a'); link.href = url; link.download = copy.handoff.file; link.click(); URL.revokeObjectURL(url);
  };
  const mail = CONTACT_EMAIL ? `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(`${copy.handoff.subject} — ${draft.company.trim()}`)}&body=${encodeURIComponent(summary)}` : null;

  return (
    <LocaleProvider locale={locale} page="intake">
      <a className="skip-link" href="#contenido">{SITE[locale].skip}</a>
      <SiteHeader />
      <main id="contenido" className="intake" tabIndex={-1}>
        <div className="wrap intake__grid">
          <aside className="intake__aside">
            <BrandAssembly tone="dark" showWordmark={false} autoPlay="FRAGMENT_TO_INTEGRATE" label="" className="intake__mark" />
            <SectionMark label={copy.mark} />
            <h1 className="h2 intake__title">{copy.title}</h1>
            <p className="lead intake__lead">{copy.lead}</p>
            <ol role="list" className="intake__next">
              {copy.next.map((line, index) => <li key={line}><span className="mono">{String(index + 1).padStart(2, '0')}</span>{line}</li>)}
            </ol>
          </aside>
          <section className="intake__panel" aria-labelledby="intake-step-title">
            <ol role="list" className="intake__steps" aria-label={copy.stepsLabel}>
              {STEPS.map((label, index) => (
                <li key={label} className={index === step ? 'is-current' : index < step ? 'is-done' : undefined} aria-current={index === step ? 'step' : undefined}>
                  <span className="intake__step-node" aria-hidden="true" /><span className="mono">{String(index + 1).padStart(2, '0')}</span>{label}
                </li>
              ))}
            </ol>
            <h2 ref={heading} id="intake-step-title" className="h3 intake__step-title" tabIndex={-1}>{STEPS[step]}</h2>
            <form className="intake__form" noValidate onSubmit={advance}>
              {step === 0 && (
                <div className="intake__fields">
                  <fieldset className="intake__choice intake__start">
                    <legend>{START[locale].label}</legend>
                    {START_IDS.map(id => (
                      <label key={id}><input type="radio" name="start" value={id} checked={draft.start === id} onChange={() => set('start', id)} /><span><strong>{START[locale].options[id].title}</strong> {START[locale].options[id].body}</span></label>
                    ))}
                  </fieldset>
                  <Field id="need" label={copy.fields.need.label} hint={isStartId(draft.start) ? START[locale].options[draft.start].hint : copy.fields.need.hint} error={errors.need} optionalLabel={copy.optional}>
                    {props => <textarea {...props} rows={7} maxLength={2000} value={draft.need} onChange={event => set('need', event.target.value)} />}
                  </Field>
                </div>
              )}
              {step === 1 && (
                <div className="intake__fields">
                  <Field id="category" label={copy.fields.category} error={errors.category} optionalLabel={copy.optional}>
                    {props => (
                      <select {...props} value={draft.category} onChange={event => set('category', event.target.value)}>
                        <option value="">{copy.fields.select}</option>{categories(locale).map(([id, label]) => <option key={id} value={id}>{label}</option>)}
                      </select>
                    )}
                  </Field>
                  <fieldset className="intake__choice">
                    <legend>{copy.fields.scope}</legend>
                    {SCOPES.map(([value, label]) => (
                      <label key={value}><input type="radio" name="scope" value={value} checked={draft.scope === value} onChange={() => set('scope', value)} /><span>{label}</span></label>
                    ))}
                  </fieldset>
                  <Field id="quantity" label={copy.fields.quantity} optional optionalLabel={copy.optional}>
                    {props => <input {...props} value={draft.quantity} onChange={event => set('quantity', event.target.value)} />}
                  </Field>
                  <Field id="reference" label={copy.fields.reference} optional optionalLabel={copy.optional}>
                    {props => <input {...props} value={draft.reference} onChange={event => set('reference', event.target.value)} />}
                  </Field>
                  <Field id="city" label={copy.fields.city} error={errors.city} optionalLabel={copy.optional}>
                    {props => <input {...props} autoComplete="address-level2" value={draft.city} onChange={event => set('city', event.target.value)} />}
                  </Field>
                  <Field id="date" label={copy.fields.date.label} optional hint={copy.fields.date.hint} optionalLabel={copy.optional}>
                    {props => <input {...props} type="month" value={draft.date} onChange={event => set('date', event.target.value)} />}
                  </Field>
                </div>
              )}
              {step === 2 && (
                <div className="intake__fields">
                  <Field id="company" label={copy.fields.company} error={errors.company} optionalLabel={copy.optional}>{props => <input {...props} autoComplete="organization" value={draft.company} onChange={event => set('company', event.target.value)} />}</Field>
                  <Field id="name" label={copy.fields.name} error={errors.name} optionalLabel={copy.optional}>{props => <input {...props} autoComplete="name" value={draft.name} onChange={event => set('name', event.target.value)} />}</Field>
                  <Field id="role" label={copy.fields.role} optional optionalLabel={copy.optional}>{props => <input {...props} autoComplete="organization-title" value={draft.role} onChange={event => set('role', event.target.value)} />}</Field>
                  <Field id="email" label={copy.fields.email} error={errors.email} optionalLabel={copy.optional}>{props => <input {...props} type="email" autoComplete="email" inputMode="email" value={draft.email} onChange={event => set('email', event.target.value)} />}</Field>
                  <Field id="phone" label={copy.fields.phone} optional optionalLabel={copy.optional}>{props => <input {...props} type="tel" autoComplete="tel" inputMode="tel" value={draft.phone} onChange={event => set('phone', event.target.value)} />}</Field>
                  <div className="intake__consent">
                    <label>
                      <input id="intake-consent" type="checkbox" checked={draft.consent} aria-invalid={Boolean(errors.consent)} aria-describedby={errors.consent ? 'intake-consent-error' : undefined} onChange={event => set('consent', event.target.checked)} />
                      <span>{copy.fields.consent}</span>
                    </label>
                    {errors.consent && <p id="intake-consent-error" className="intake__error" role="alert">{errors.consent}</p>}
                  </div>
                </div>
              )}
              {step === 3 && (
                <div className="intake__summary">
                  <dl>
                    <Summary label={copy.summary.need} edit={copy.summary.edit} value={[isStartId(draft.start) ? START[locale].options[draft.start].title : '', draft.need].filter(Boolean).join(' · ')} onEdit={() => go(0)} />
                    <Summary label={copy.summary.context} edit={copy.summary.edit} value={[categoryLabel(draft.category, locale), scopeLabel(draft.scope, locale), draft.quantity, draft.city, draft.date].filter(Boolean).join(' · ')} onEdit={() => go(1)} />
                    <Summary label={copy.summary.contact} edit={copy.summary.edit} value={[draft.company, draft.name, draft.role, draft.email, draft.phone].filter(Boolean).join(' · ')} onEdit={() => go(2)} />
                  </dl>
                  <div className="intake__handoff" role="status">
                    {mail ? (
                      <>
                        <p>{copy.handoff.mail}</p>
                        <a className="cta cta--primary" href={mail}><span>{copy.handoff.send}</span><ArrowGlyph /></a>
                      </>
                    ) : (
                      <>
                        <p><strong>{copy.handoff.pendingStrong}</strong> {copy.handoff.pending}</p>
                        <div className="intake__handoff-actions">
                          <button type="button" className="cta cta--primary" onClick={copySummary}><span>{copied ? copy.handoff.copied : copy.handoff.copy}</span><ArrowGlyph /></button>
                          <button type="button" className="cta cta--quiet" onClick={download}><span>{copy.handoff.download}</span><ArrowGlyph /></button>
                        </div>
                      </>
                    )}
                    <p className="intake__fine">{copy.handoff.fine}</p>
                  </div>
                </div>
              )}
              <div className="intake__nav">
                {step > 0 && <button type="button" className="cta cta--quiet" onClick={() => go(step - 1)}><span>{copy.nav.back}</span></button>}
                {step < STEPS.length - 1 && <button type="submit" className="cta cta--primary"><span>{step === STEPS.length - 2 ? copy.nav.review : copy.nav.next}</span><ArrowGlyph /></button>}
              </div>
            </form>
          </section>
        </div>
      </main>
      <SiteFooter />
    </LocaleProvider>
  );
}

function Field({ id, label, hint, error, optional, optionalLabel, children }: {
  id: string; label: string; hint?: string; error?: string; optional?: boolean; optionalLabel: string;
  children: (props: { id: string; name: string; 'aria-invalid': boolean; 'aria-describedby'?: string; required?: boolean }) => ReactNode;
}) {
  const fieldId = `intake-${id}`; const hintId = useId(); const errorId = `${fieldId}-error`;
  const described = [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(' ') || undefined;
  return (
    <div className="intake__field">
      <label htmlFor={fieldId}>{label}{optional && <span className="intake__optional"> · {optionalLabel}</span>}</label>
      {hint && <p id={hintId} className="intake__hint">{hint}</p>}
      {children({ id: fieldId, name: id, 'aria-invalid': Boolean(error), 'aria-describedby': described, required: optional ? undefined : true })}
      {error && <p id={errorId} className="intake__error" role="alert">{error}</p>}
    </div>
  );
}

function Summary({ label, edit, value, onEdit }: { label: string; edit: string; value: string; onEdit: () => void }) {
  return (
    <div>
      <dt>{label}</dt>
      <dd>{value || '—'}</dd>
      <button type="button" className="intake__edit" onClick={onEdit}>{edit}<span className="visually-hidden"> {label.toLowerCase()}</span></button>
    </div>
  );
}
